import { useTranslation } from 'react-i18next';
import DefaultIcon2 from '$components/DefaultIcon2/DefaultIcon2';
import { SIMERA_FEE } from '$constants/SimeraOperations';
import capitalizedName from '$utils/capitalizedName';
import computeYearsOfExperience from '$utils/computeYearsOfExperience';

import { MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { GetSharedCandidatesRosterDataOutput } from '$store/modules/candidates';
import { countriesCodes } from '$constants/countriesCodes';
import { ReactComponent as Login } from '$icons/uncolored/login.svg';
import SPKCandidateVideos from '../SPKCandidateVideos/SPKCandidateVideos';
import SPKCandidateCardSkill from './SPKCandidateSkills';
import SPKCandidateHiddenSkills from './SPKCandidateHiddenSkills';

const SPKCandidateCard = ({
  candidate,
  showSidebarHandler,
  showVideoModalHandler,
  showLoginOrRegisterModalHandler,
}: {
  candidate: GetSharedCandidatesRosterDataOutput['candidates'][number];
  showSidebarHandler: (
    candidate: GetSharedCandidatesRosterDataOutput['candidates'][number],
    showSmartScore?: boolean
  ) => void;
  showVideoModalHandler: (
    candidate: GetSharedCandidatesRosterDataOutput['candidates'][number]
  ) => void;
  showLoginOrRegisterModalHandler: (
    candidate: GetSharedCandidatesRosterDataOutput['candidates'][number]
  ) => void;
}) => {
  const [t] = useTranslation('global');
  const [overflowStart, setOverflowStart] = useState<number>();
  const [hiddenSkills, setHiddenSkills] = useState<
    GetSharedCandidatesRosterDataOutput['candidates'][number]['CandidateSkills']
  >([]);
  const [visibleSkills, setVisibleSkills] = useState<
    GetSharedCandidatesRosterDataOutput['candidates'][number]['CandidateSkills']
  >([]);

  const skillsContainerRef = useRef<HTMLDivElement | null>(null);
  const videoParentRef = useRef<HTMLDivElement | null>(null);

  const yearsOfExperience = useMemo(() => {
    return computeYearsOfExperience(candidate.CandidateWorkExperience);
  }, [candidate.CandidateWorkExperience]);

  const onOverflowHandler = (index: number) => {
    setOverflowStart(index);
  };

  const handleClickCard = (
    candidateData: GetSharedCandidatesRosterDataOutput['candidates'][number],
    showSmartScore = false
  ) => {
    showSidebarHandler(candidateData, showSmartScore);
  };

  const countryEmoji = useMemo(() => {
    return countriesCodes.find(
      (singleCountry) =>
        singleCountry.name.toLowerCase() ===
        candidate?.ResidenceCountry?.toLowerCase()
    )?.emoji;
  }, [candidate?.ResidenceCountry]);

  useEffect(() => {
    if (!candidate.CandidateSkills) return;

    if (!overflowStart && candidate.CandidateSkills) {
      setVisibleSkills(candidate.CandidateSkills);
      return;
    }

    const visible = candidate.CandidateSkills.slice(0, overflowStart);
    const hidden = candidate.CandidateSkills.slice(overflowStart);

    setVisibleSkills(visible);
    setHiddenSkills(hidden);
  }, [candidate.CandidateSkills, overflowStart]);

  return (
    <div
      data-testid="spkCandidateCard"
      role="button"
      tabIndex={0}
      onKeyDown={() => {}}
      onClick={(ev: MouseEvent<HTMLDivElement>) => {
        if ((ev.target as HTMLElement).nodeName.toLowerCase() !== 'video')
          handleClickCard(candidate);
      }}
      className="flex flex-row"
    >
      <div className="bg-white rounded-l-2xl flex flex-row p-4 divide-x-2 divide-gray-100 *:px-2 w-3/4 sm:w-5/6">
        <div className="flex flex-col flex-1 w-full lg:w-2/3">
          {/* Name, last job and country */}
          <div className="flex flex-row gap-4 mb-8 items-center">
            <div className="w-10 min-w-10">
              <DefaultIcon2
                isEmployer
                firstName={candidate.User?.FirstName}
                lastName={candidate.User?.LastName}
                fontSize="text-xl"
                hideBorder
                picture={candidate?.User?.ProfilePicture?.Url}
              />
            </div>
            <div className="flex flex-col sm:flex-row sm:divide-x divide-gray-400 *:px-4 -mx-4 sm:items-center">
              <div className="flex font-bold text-base">
                {`${capitalizedName(
                  candidate.User?.FirstName
                )} ${capitalizedName(candidate.User?.LastName)}.`}
              </div>
              <div className="font-bold flex flex-row">
                <span className="mr-4">
                  {candidate?.CandidateWorkExperience?.[0]?.JobTitle}
                </span>
                <span className="text-nowrap font-light text-xs border border-neutral-200 text-neutral-600 rounded-md px-2 shadow flex items-center self-center">
                  {countryEmoji} {candidate.ResidenceCountry}
                </span>
              </div>
            </div>
          </div>

          {/* General Info */}
          <div className="text-sm mb-2">
            <div className="flex flex-row divide-x divide-gray-400 *:px-2 -mx-2">
              {candidate?.CandidateWorkExperience?.[0]?.Company && (
                <span className="flex flex-wrap text-black/50">
                  Highlight:{' '}
                  <span className="text-black ml-1">
                    {candidate?.CandidateWorkExperience?.[0]?.Company}
                  </span>
                </span>
              )}
              <span>
                <span className="text-black/50">Exp:</span> {yearsOfExperience}+
                yrs
              </span>
              <span>
                <span className="text-black/50">Monthly Salary (USD):</span> $
                {(
                  (candidate?.CandidateJobInterest?.SalaryLowerBound || 0) +
                  SIMERA_FEE
                ).toLocaleString('en-US', {
                  style: 'decimal',
                  currency: 'USD',
                })}{' '}
                - $
                {(
                  (candidate?.CandidateJobInterest?.SalaryUpperBound || 0) +
                  SIMERA_FEE
                ).toLocaleString('en-US', {
                  style: 'decimal',
                  currency: 'USD',
                })}
                /m USD
              </span>
            </div>
          </div>
          {/* Skills */}
          <div className="flex flex-row items-center gap-2 mb-4">
            <div
              className="flex overflow-x-hidden gap-2 text-xs font-semibold text-gray-500 max-w-full"
              ref={skillsContainerRef}
            >
              {visibleSkills &&
                visibleSkills
                  .slice(0, overflowStart)
                  .map((singleSkill, index) => {
                    return (
                      <SPKCandidateCardSkill
                        key={`${singleSkill.Skill.title}_${singleSkill.Id}`}
                        skill={singleSkill.Skill.title}
                        index={index}
                        containerRef={
                          skillsContainerRef.current as HTMLDivElement
                        }
                        setOverflowIndex={onOverflowHandler}
                        isVerified={singleSkill.IsVerified}
                      />
                    );
                  })}
            </div>
            {hiddenSkills && hiddenSkills.length > 0 && (
              <SPKCandidateHiddenSkills
                hiddenSkills={hiddenSkills}
                uniqueId={candidate.Id}
              />
            )}
          </div>
          <div className="flex flex-row mt-auto text-sm divide-x divide-neutral-400/50 *:px-4 -mx-4 font-light">
            {candidate?.AdditionalVideos &&
              candidate.AdditionalVideos.length > 0 && (
                <button
                  type="button"
                  className="flex items-center group"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    showVideoModalHandler(candidate);
                  }}
                >
                  <span className="group-hover:underline">
                    {t('sharedPosition.sharedCandidatesRoster.watchVideo')}
                  </span>
                  <i className="pi pi-video ml-2 text-spk-brightBlue" />
                </button>
              )}
            {candidate.AIData && (
              <button
                type="button"
                className="flex items-center group"
                onClick={(ev) => {
                  ev.stopPropagation();
                  handleClickCard(candidate, true);
                }}
              >
                <span className="group-hover:underline">AI Scoring</span>
                <i className="pi pi-sparkles ml-2 text-spk-brightBlue" />
              </button>
            )}
            <button type="button" className="flex items-center group">
              <span className="group-hover:underline">
                {t('sharedPosition.sharedCandidatesRoster.fullProfile')}
              </span>
              <i className="pi pi-eye ml-2 text-spk-brightBlue" />
            </button>
          </div>
        </div>
        {candidate.AdditionalVideos &&
          candidate.AdditionalVideos.length > 0 && (
            <div
              id={`candidate_${candidate.Id}_videos`}
              className="flex-0 ml-auto hidden lg:block w-1/3"
              ref={videoParentRef}
            >
              <div className="max-w-72 m-auto">
                <SPKCandidateVideos videos={candidate.AdditionalVideos} />
              </div>
            </div>
          )}
      </div>
      <button
        onClick={(ev) => {
          ev.stopPropagation();
          showLoginOrRegisterModalHandler(candidate);
        }}
        type="button"
        className="flex flex-row p-4 bg-[#FAFAF7] rounded-r-2xl w-1/4 sm:w-1/6 justify-center items-center content-center text-sm text-center text-spk-brightBlue font-semibold"
      >
        <Login className="justify-start min-w-5 w-5 min-h-5 h-5 mr-2" />
        Login to Request Interview
      </button>
    </div>
  );
};

export default SPKCandidateCard;
